/**
 * k-anonymity suppression.
 *
 * THIS FILE IS A CONTRACTUAL COMMITMENT, NOT A PREFERENCE.
 * P3's Data Sharing Agreement, Section V, commits that "dashboard cells,
 * reporting categories, or analytics segments representing fewer than ten (10)
 * individuals may be suppressed, rolled up, masked, or otherwise aggregated".
 * Institutional review boards, HBCU IRBs in particular, are told this is how
 * P3 protects their students. Changing K is a contract amendment.
 *
 * ── Why the type exists ───────────────────────────────────────────────────
 * An earlier design returned plain numbers and guarded them with a test that
 * keyed on one field name. Three sub-10 figures in the same payload used
 * different field names, so CI went green while the payload broke the
 * contract. `Cell<T>` makes that failure unrepresentable: a raw number cannot
 * reach a response body, because response types are declared in terms of
 * Cell. If you find yourself unwrapping a Cell to put a number somewhere,
 * that is the bug, not the type.
 *
 * ── The three ways a figure can fail to exist ─────────────────────────────
 *   suppressed    we know it and may not say it (privacy)
 *   unavailable   we do not know it (no source, or the source is empty)
 * These are different states and they read differently to a customer. A
 * customer who sees "unavailable" where they should see "suppressed" thinks
 * the product is broken; the reverse makes P3 look like it is hiding a
 * capability it does not have.
 */

/** The suppression floor. Contractual. See the file header before changing. */
export const K = 10;

export type SuppressionReason =
  /** The figure itself is 1..K-1. */
  | "below-threshold"
  /** Safe alone, withheld so another suppressed cell cannot be derived. */
  | "complementary"
  /** A residual whose parts are pinned by arithmetic. */
  | "remainder-pinned";

export type Cell<T> =
  | { readonly kind: "value"; readonly value: T }
  | { readonly kind: "suppressed"; readonly reason: SuppressionReason }
  | { readonly kind: "unavailable"; readonly reason: string };

export const value = <T>(v: T): Cell<T> => ({ kind: "value", value: v });

export const suppressed = (reason: SuppressionReason): Cell<never> => ({
  kind: "suppressed",
  reason,
});

export const unavailable = (reason: string): Cell<never> => ({
  kind: "unavailable",
  reason,
});

export const isValue = <T>(c: Cell<T>): c is { kind: "value"; value: T } =>
  c.kind === "value";

/**
 * A headcount.
 *
 * Zero is publishable: "no student has been matched yet" describes an absence,
 * not a person, and withholding it would make an empty dashboard unreadable.
 * 1..K-1 is not publishable, including the cohort's own total. There is no
 * exemption for "it is only the total" — that was argued for and rejected,
 * because "you have 4 students" and "we never show fewer than 10" cannot both
 * be true on the same product.
 */
export function count(n: number): Cell<number> {
  if (!Number.isFinite(n) || n < 0) return unavailable("not a valid count");
  if (n === 0) return value(0);
  return n < K ? suppressed("below-threshold") : value(n);
}

/**
 * A rate, published with its parts so a reader can see what it is built on.
 *
 * Both the numerator and the denominator must independently survive
 * suppression. A rate over a safe denominator still leaks: 3/40 published as
 * 7.5% multiplies straight back to 3.
 */
export interface Rate {
  readonly numerator: number;
  readonly denominator: number;
  readonly rate: number;
}

export function rate(numerator: number, denominator: number): Cell<Rate> {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator)) {
    return unavailable("not a valid rate");
  }
  if (denominator <= 0) return unavailable("no denominator");
  if (denominator < K) return suppressed("below-threshold");
  // A zero numerator is a real, publishable answer over a safe denominator.
  if (numerator !== 0 && numerator < K) return suppressed("below-threshold");
  // The complement leaks the same way: 40 of 43 matched means 3 did not.
  const complement = denominator - numerator;
  if (complement !== 0 && complement < K) return suppressed("complementary");
  return value({ numerator, denominator, rate: numerator / denominator });
}

export interface Bucket {
  readonly label: string;
  readonly n: number;
}

export interface SuppressedBuckets {
  /** Buckets safe to publish, in the order given. */
  readonly shown: ReadonlyArray<{ label: string; n: number }>;
  /** How many buckets were withheld. Visible in the UI as rows, so treat as public. */
  readonly hiddenCount: number;
  /**
   * The sum of the withheld buckets, when publishing it cannot pin their
   * values. Never published alongside anything that would let a reader solve
   * for an individual bucket.
   */
  readonly hiddenSum: Cell<number>;
  /** The total across every bucket, suppressed if the cohort itself is small. */
  readonly total: Cell<number>;
}

/**
 * Complementary suppression over a set of buckets.
 *
 * ── The thing that is easy to get wrong ───────────────────────────────────
 * An earlier version of this function withheld the hidden buckets' SUM,
 * believing that protected them. It does not. Once the total is published and
 * every surviving bucket is published, the hidden sum is arithmetic:
 * `total - Σ shown`. Withholding it changes nothing an adversary can compute.
 *
 * So the sum is always published, because hiding it was theatre. The real
 * defence is to make the hidden sum UNINFORMATIVE, by choosing which buckets
 * to hide:
 *
 *  1. SUBTRACTION. [22, 12, 6] with a published total of 40: hiding only the 6
 *     publishes it as 40-22-12. At least two buckets are always hidden.
 *
 *  2. PINNING. Two hidden buckets summing to 18, each necessarily in [1,9],
 *     can only be 9 and 9. So after choosing a hidden set, count how many
 *     value assignments are consistent with its sum. If fewer than
 *     MIN_DISTRIBUTIONS, pull in the next bucket and count again. Pulling in a
 *     bucket at or above K makes the space large immediately, because that
 *     bucket has no upper bound.
 *
 * Buckets at zero are published as zero. A zero describes an absence, not a
 * person, and hiding it would make an early dashboard unreadable.
 *
 * Hidden buckets are never named. `shown` carries labels; the hidden ones are
 * counted only. "Nursing: suppressed" tells a reader which programs are small,
 * which is disclosive on its own in a small cohort.
 */
export function suppressBuckets(buckets: ReadonlyArray<Bucket>): SuppressedBuckets {
  const clean = buckets.filter((b) => Number.isFinite(b.n) && b.n >= 0);
  const grandTotal = clean.reduce((s, b) => s + b.n, 0);
  const total = count(grandTotal);

  if (clean.length === 0) {
    return { shown: [], hiddenCount: 0, hiddenSum: unavailable("no data"), total };
  }

  // A cohort below the floor has no publishable shape at all. Emitting
  // per-bucket zeroes here would be a leak by omission.
  if (!isValue(total)) {
    return {
      shown: [],
      hiddenCount: clean.length,
      hiddenSum: suppressed("below-threshold"),
      total,
    };
  }

  const unsafe = (n: number) => n >= 1 && n < K;

  // Ascending among the candidates, so each extra sacrifice is the cheapest
  // one left. Zeros are never candidates: they are safe and informative.
  const asc = [...clean].sort((a, b) => a.n - b.n);
  const hidden: Bucket[] = asc.filter((b) => unsafe(b.n));

  if (hidden.length === 0) {
    return {
      shown: clean.map((b) => ({ label: b.label, n: b.n })),
      hiddenCount: 0,
      hiddenSum: unavailable("nothing suppressed"),
      total,
    };
  }

  const pool = asc.filter((b) => !unsafe(b.n) && b.n > 0);
  // (1) then (2): grow the hidden set until it is both plural and ambiguous.
  while (
    (hidden.length < 2 || distributions(hidden) < MIN_DISTRIBUTIONS) &&
    pool.length > 0
  ) {
    hidden.push(pool.shift()!);
  }

  const hiddenSet = new Set(hidden.map((b) => b.label));
  const shown = clean.filter((b) => !hiddenSet.has(b.label));
  const hiddenSum = hidden.reduce((s, b) => s + b.n, 0);

  // Nothing survived, or the set is still pinned because there was nothing
  // left to pull in. Either way there is no breakdown to publish.
  if (shown.length === 0 || distributions(hidden) < MIN_DISTRIBUTIONS) {
    return {
      shown: [],
      hiddenCount: clean.length,
      hiddenSum: suppressed("remainder-pinned"),
      total,
    };
  }

  return {
    // Caller's original order, which is meaningful: a rank, or a taxonomy the
    // customer reads in a fixed sequence.
    shown: shown.map((b) => ({ label: b.label, n: b.n })),
    hiddenCount: hidden.length,
    hiddenSum: value(hiddenSum),
    total,
  };
}

/**
 * How many distinct value assignments are consistent with what a reader can
 * derive: the hidden set's size and its sum.
 *
 * Three is not a magic number, it is the smallest count at which no single
 * bucket's value is forced. At two, an adversary often knows the multiset even
 * if not which label holds which value.
 */
const MIN_DISTRIBUTIONS = 3;

/** Counted exactly up to the cap, then abandoned: past the cap the answer is "plenty". */
const DISTRIBUTION_CAP = 64;

function distributions(hidden: ReadonlyArray<Bucket>): number {
  const c = hidden.length;
  if (c < 2) return 1;
  const sum = hidden.reduce((s, b) => s + b.n, 0);
  // A hidden bucket at or above K has no upper bound a reader can assume, so
  // the space is immediately large. This is why pulling in a big bucket works.
  if (hidden.some((b) => b.n >= K)) return DISTRIBUTION_CAP;

  // Partitions of `sum` into exactly `c` unordered parts, each in [1, K-1].
  // Unordered, because the labels are withheld: a reader learns the multiset
  // of values at best, never which bucket holds which.
  const memo = new Map<string, number>();
  const walk = (remaining: number, parts: number, min: number): number => {
    if (parts === 0) return remaining === 0 ? 1 : 0;
    if (remaining < min * parts || remaining > (K - 1) * parts) return 0;
    const key = `${remaining}:${parts}:${min}`;
    const hit = memo.get(key);
    if (hit !== undefined) return hit;
    let n = 0;
    for (let v = min; v <= K - 1 && n < DISTRIBUTION_CAP; v++) {
      n += walk(remaining - v, parts - 1, v);
    }
    const capped = Math.min(n, DISTRIBUTION_CAP);
    memo.set(key, capped);
    return capped;
  };
  return walk(sum, c, 1);
}
